import type { TCoordinate } from './address'
import type { TStopAppointment } from './appointment'
import type { ICarriers } from './carrier'
import type { ChaineDistanceType } from './distance'
import type { IEquipment } from './equipment'
import type { TStopType } from './post'
import type { ShipmentTrackingSourceEnum, TNextStopDetails, TShipmentTrackingSourceTypes } from './tracking'
import type { IMembership, UserDetails } from './user'
import type { IWeight } from './weight'
import type { IWorkspace } from './workspace'

export interface IAddress {
  /** Address label or the full address (i.e. 2554 Smart Way, San Jose, CA 95148 US) */
  addressLabel: string | null
  /**
   * Array of bounding box coordinates where this result is located. This can be used for Geo-Fence alerts and also shown on the map for a better experience/
   * The order is as below:
   * - min lat / bottom-left Latitude
   * - max lat / top-right Latitude
   * - min lon / bottom-left Longitude
   * - max lon / top-right Longitude
   */
  boundingBox?: [string, string, string, string]

  /** The city */
  city: string | null

  /** The address coordinates */
  coordinates: TCoordinate | null

  /** Long country name i.e. United States */
  country: string | null

  /** Short country code i.e. US */
  countryCode: string | null

  /** The county */
  county: string | null

  /** The postal or zip code */
  postalCode: string | null

  /** The suffix for the zip code i.e. 95148-1234 (1234 is the suffix) */
  postalCodeSuffix: string | null

  /** long state name */
  state: string | null

  /** short state name. For state California, this is CA */
  stateCode: string | null

  /** The street */
  street: string | null

  /** The number on the street */
  streetNumber: string | null
}

export interface IStopWarehouseGeofence {
  /**
   * The geofence polygon coordinates
   */
  property?: { latitude: number; longitude: number }[] | null
  /**
   * The geofence radius in miles (for radius type)
   */
  radius?: number | null
  /**
   * The type of geofence shape: 'polygon' or 'radius'
   */
  type?: 'polygon' | 'radius' | null
}

export interface IStopWarehouse {
  /**
   * The address of the warehouse
   */
  address: IAddress | null
  /**
   * The category of the warehouse location
   */
  category: string[] | null
  /**
   * The geofence configuration
   */
  geofence?: IStopWarehouseGeofence | null
  /**
   * The Google map place id of the warehouse
   */
  locationID: string | null
  /**
   * The name of the warehouse
   */
  name: string | null
  /**
   * The Google map place id of the warehouse
   */
  placeID: string | null
  /**
   * The rating of the warehouse (e.g. 5 (5 start rating))
   */
  rating: number | null
  /**
   * The reviews of the warehouse (e.g. 129 reviews)
   */
  reviews: number | null
  /**
   * The thumbnail of the warehouse
   */
  thumbnail: string | null
  /**
   * Active warehouse hours
   */
  warehouseHours?: string | null
}

export interface IStop {
  appointment: TStopAppointment | null
  arrivalDateTime: {
    date: string
    time?: string
    timezone?: string
  } | null
  departureDateTime: {
    date: string
    time?: string
    timezone?: string
  } | null
  eta: {
    date: string
    time?: string
    timezone?: string
  } | null
  id: string
  instructions: string | null
  referenceNumber: string | null
  sequence: number
  type: TStopType | null
  warehouse: IStopWarehouse | null
}

/**
 * Shipment member associated with the shipment
 */
export type TShipmentMember = {
  membership: IMembership | null
  user: UserDetails
  workspace: IWorkspace | null
}

export type TTeamMember = UserDetails

export type TDriver = TShipmentMember

export type TDispatcher = TShipmentMember

export enum ShipmentUserRole {
  Dispatcher = 'Dispatcher',
  Driver = 'Driver',
  TeamMember = 'Team Member'
}

export enum MobileAppTrackingStatusEnum {
  /**
   * The driver is awaiting app installation
   */
  AWAITING_APP_INSTALL = 'AWAITING_APP_INSTALL',

  /**
   * The driver is awaiting permissions
   */
  AWAITING_PERMISSIONS = 'AWAITING_PERMISSIONS',

  /**
   * The driver has incorrect permissions
   */
  INCORRECT_PERMISSIONS = 'INCORRECT_PERMISSIONS',

  /**
   * The driver is logged out
   */
  LOGGED_OUT = 'LOGGED_OUT',

  /**
   * The driver is not tracking
   */
  NOT_TRACKING = 'NOT_TRACKING',

  /**
   * The driver is sending mock locations
   */
  SENDING_MOCK_LOCATION = 'SENDING_MOCK_LOCATION',

  /**
   * The driver is tracking
   */
  TRACKING = 'TRACKING',

  /**
   * The driver tracking has stopped
   */
  TRACKING_STOPPED = 'TRACKING_STOPPED',

  /**
   * The driver is uninstalled
   */
  UNINSTALLED = 'UNINSTALLED',

  /**
   * The driver is waiting for the first ping
   */
  WAITING_FIRST_PING = 'WAITING_FIRST_PING'
}

export interface IReferenceNumbers {
  billOfLading: string | null
  billingReferenceNumber: string | null
  /**
   * This field is used to store any reference numbers that are to be used for sending tracking data to external systems. For example, if we are sending data to FourKites, Project44 or MacroPoint, and they require chaine customer's customer's load number (i.e. the shipper's load number), we would map and store that here.
   */
  externalTrackingID: string | null
  orderNumber: string | null

  /**
   * Often times there is a pickup number on the shipment level. This is sometimes the customer's reference number as well.
   */
  pickupNumber: string | null
  /**
   * A Progressive Rotating Order(PRO) Number. Commonly known as a PRO Number.
   * A PRO number is a unique identifier assigned by a carrier (typically a freight or trucking company) to a specific shipment. The PRO number is used to track the shipment's progress through the carrier's system, providing visibility for both the shipper and the receiver. It helps to keep track of the shipment's location, status, and estimated delivery date, and can be used to file claims in case of any issues with the shipment.
   */
  progressiveRotatingOrderNumber: string | null
  /**
   * Also called the PO Number
   * A PO number is a unique identifier assigned to a purchase order, which is a document issued by a buyer to a seller, indicating the types, quantities, and agreed-upon prices for products or services. The purchase order acts as a legally binding contract between the buyer and the seller. The PO number is used to track and manage the order throughout the entire purchasing process, from order placement to payment and receipt of goods.
   */
  purchaseOrderNumber: string | null
}

export enum ShipmentTabEnum {
  Active = 'Active',
  Closed = 'Closed'
}

export enum ShipmentStatusEnum {
  Canceled = 'Canceled', // adding for backwards compatibility
  CarrierAssigned = 'Carrier assigned',
  Completed = 'Completed',
  Delivered = 'Delivered',
  Expired = 'Expired',
  // adding for backwards compatibility
  InTransit = 'In transit',
  NeedsCarrier = 'Needs carrier',

  Pending = 'Pending'
}

export enum StopStatusEnum {
  Arrived = 'Arrived',
  Departed = 'Departed',
  Enroute = 'En route'
}

export enum TrackingStatusEnum {
  IncorrectPermissions = 'Incorrect Permissions'
}

/**
 * An enum indicating how the shipment entity was created
 */
export enum ShipmentCreatedViaEnum {
  ChaineWebApp = 'ChaineWebApp',
  Integration = 'Integration'
}

export interface IAddressDetails {
  address?: string
  city?: string
  country?: string
  name?: string
  state?: string
  zip?: string
}

export interface ICommodityDetails {
  chassisNumbers?: string[]
  description?: string
  dimensions?: {
    height?: number
    length?: number
    width?: number
  }
  quantity?: number
  unit?: string
  weight?: number
  weightUnit?: string
}

export interface IContainerDetails {
  chassisNumber?: string
  containerNumber?: string
  containerSize?: string
  containerType?: string
}

export interface IBillOfLadingExtractedData {
  billOfLadingNumber?: string
  bookingNumber?: string
  commodityDetails?: ICommodityDetails
  consignee?: IAddressDetails
  container?: IContainerDetails
  poNumber?: string
  portOfLoading?: string
  shipmentNumber?: string
  shipper?: IAddressDetails
  voyageNumber?: string
}

export interface ICarrierInvoiceExtractedData {
  hasAccessorialCosts?: boolean
}

export interface ILumperReceiptExtractedData {
  amount?: string
}

export interface ILoadedImageExtractedData {
  description?: string
  isChained?: boolean
  isStrapped?: boolean
  isTarped?: boolean
  trailerNumber?: string
  trailerType?: string
}

export interface IEquipmentDetails {
  chassisNumbers?: string[]
  equipmentDescription?: string
  equipmentType?: string
  isSealed?: boolean
  truckNumber?: string
}

export interface ISealImageExtractedData {
  sealNumber?: string
}

export interface IExceptionImageExtractedData {
  exceptionDescription?: string
  exceptionSeverity?: string
  exceptionType?: string
  isFreightDamaged?: boolean
}

export interface IInGateReceiptExtractedData {
  equipmentDetails?: IEquipmentDetails
  interchangeNumber?: string
}

export interface IOutGateReceiptExtractedData {
  container?: IContainerDetails
}

export type TAttachmentExtractedData =
  | IBillOfLadingExtractedData
  | ICarrierInvoiceExtractedData
  | IExceptionImageExtractedData
  | IInGateReceiptExtractedData
  | ILoadedImageExtractedData
  | ILumperReceiptExtractedData
  | IOutGateReceiptExtractedData
  | ISealImageExtractedData
  | null

export enum FileClassificationTypeEnum {
  BillOfLading = 'Bill of lading',
  CarrierInvoice = 'Carrier invoice',
  ExceptionImage = 'Exception image',
  InGateReceipt = 'In gate receipt',
  LoadedImage = 'Loaded image',
  LumperReceipt = 'Lumper receipt',
  NotRelevant = 'Not relevant',
  Other = 'Other',
  OtherReceipt = 'Other receipt',
  OutGateReceipt = 'Out gate receipt',
  ProofOfDelivery = 'Proof of delivery',
  ScaleTicket = 'Scale ticket',
  SealImage = 'Seal image'
}

export enum ChatMessageEventSourceEnum {
  Chat = 'Chat',
  Email = 'Email',
  SMS = 'SMS'
}

export interface IShipmentFile {
  classificationType: FileClassificationTypeEnum
  extractedData?: TAttachmentExtractedData
  senderUserID: string
  source: ChatMessageEventSourceEnum
  timestamp: string
  url: string
}

export type TFiles = { [key: string]: IShipmentFile }
export type TShipmentFiles = IShipmentFile[] | TFiles | null | undefined

export interface IShipment {
  carriers?: ICarriers
  commodity?: string
  createdVia?: ShipmentCreatedViaEnum
  customer: {
    name: string
  } | null
  destination: IStop | null
  dispatchers: TDispatcher[] | []
  drivers: TDriver[] | []
  equipment?: IEquipment | null
  files?: TShipmentFiles
  id: string

  isShipmentOwner: boolean
  lastLocation?: {
    city: string
    state: string
    timestamp: string
  } | null
  origin: IStop | null
  /**
   * The post ID if it exists. This will always be there for loads created from the TMS and are eligible for "Booking"
   */
  postID?: string
  priority?: string
  referenceNumbers?: IReferenceNumbers
  release?: string

  shipmentMode: string | null

  shipmentNumber: string
  shipmentOwnerWorkspace: IWorkspace | null
  shipmentStatus: ShipmentStatusEnum
  stops?: IStop[]
  teamMembers: TTeamMember[] | []
  tmsURL?: string
  trackingMapURL: string
  trackingStatus: {
    isTracking: boolean
    status?: TrackingStatusEnum
  }
  trailerNumber?: string | null
  truckNumber?: string | null
  weight: IWeight | null
  distance: ChaineDistanceType | null
  userRole?: ShipmentUserRole
  workspace: IWorkspace | null
}

export type TShipmentTrackingReturnType = {
  data: TShipmentTrackingSourceTypes[]
  lastLocation: { coordinates: TCoordinate; timestamp: string } | null
}

export interface IShipmentsPaginationData {
  dataSource?: 'CACHE' | 'DATABASE'
  hasNextPage: boolean
  paginationKey: string | null
}

export type TLocation = {
  /**
   * The city name.
   */
  city?: string
  /**
   * The coordinates of the location.
   */
  coordinates: TCoordinate
  /**
   * The two-letter country code.
   */
  countryCode?: string
  /**
   * The postal code.
   */
  postalCode?: string
  /**
   *  The two-letter state code.
   */
  stateCode?: string
  /**
   * The timestamp of the location.
   */
  timestamp: string
  /**
   * Details about the next planned stop relative to this location.
   */
  nextStopDetails?: TNextStopDetails
}

export interface IShipmentLastLocation {
  location: TLocation
  nextStopDetails?: TNextStopDetails
  source: ShipmentTrackingSourceEnum
}

export interface IShipmentWithTrackingDetails {
  areMobileAppPermissionsCorrect: boolean
  carriers?: ICarriers
  customer: {
    name: string
  } | null
  destination: IStop | null
  dispatchers: TDispatcher[] | []
  drivers: TDriver[] | []
  id: string
  isShipmentOwner: boolean
  isTracking: boolean
  isTrackingViaAPI?: boolean
  isTrackingViaDriverMobileApp?: boolean
  isTrackingViaELD?: boolean
  isTrackingViaExternalTrackingLink?: boolean
  isTrackingViaSFTP?: boolean
  isTrackingViaTMSApp?: boolean
  isTrackingViaTiveDevice?: boolean
  isTrackingViaVisibilityProvider?: boolean
  lastLocation?: IShipmentLastLocation
  lastLocationDeprecated?: IShipmentLastLocation
  mobileAppTrackingStatus?: MobileAppTrackingStatusEnum
  origin: IStop | null
  priority?: string
  referenceNumbers?: IReferenceNumbers
  release?: string
  shipmentMode: string | null
  shipmentNumber: string
  shipmentOwnerWorkspace: IWorkspace | null
  shipmentStatus: ShipmentStatusEnum
  stops?: IStop[]
  teamMembers: TTeamMember[] | []
  tmsURL?: string
  trackingEndTime?: string | null
  trackingMapURL: string
  trackingStartTime?: string | null
  trackingStatus: {
    isTracking: boolean
    status?: TrackingStatusEnum
  }
  userRole?: ShipmentUserRole
  workspace: IWorkspace | null
}

export enum ShipmentSortEnum {
  DROPOFF = 'DROPOFF',
  NEWEST = 'NEWEST',
  OLDEST = 'OLDEST',
  PICKUP = 'PICKUP'
}

/**
 * The enum to filter by isTracking
 */
export enum ShipmentTrackingStatusEnum {
  NOT_TRACKING = 'NOT_TRACKING',
  TRACKING = 'TRACKING'
}

/**
 * The return type for the shipment activity
 */
export type TShipmentActivityReturnType = {
  /**
   * The display name of the user who performed the action
   */
  actionByDisplayName?: string
  /**
   * The user who performed the action
   */
  actionByUser: UserDetails
  /**
   * The action that was performed on the field.
   * Possible values are: "created", "updated", "deleted", "removed", "added", "assigned", "unassigned", "removed"
   */
  actionType: 'created' | 'updated' | 'deleted' | 'assigned' | 'unassigned'
  /**
   * The display text of the action. i.e. Driver unassigned from shipment
   */
  displayText: string
  /**
   * The ISO date time of the event
   */
  eventISODateTime: string
  /**
   * The name of the event that that caused the action. i.e. teamMemberAddedToShipment, driverAssigned, stopAdded, shipmentCreated, etc.
   */
  eventName: string
  /**
   * The field that was modified i.e. Shipment status
   */
  field: string
  /**
   * The value of the field that the action was performed on.
   */
  value: {
    /**
     * The display text of the action
     */
    displayText?: string
    /**
     * If the value was a text, the text value
     */
    text?: string
    /**
     * If the value was a user, the user's UUID. This can be resolved to a user object by GraphQL
     */
    user?: UserDetails
    /**
     * If the value was a workspace, the workspace's UUID. This can be resolved to a workspace object by GraphQL
     */
    workspace?: IWorkspace
  }
}
